import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import softphone from '../../services/softphone';
import { contactsAPI, statusAPI } from '../../services/api';
import PostCallNote from '../calls/PostCallNote';
import { snapshotKnownIds, queueCallNote, resumePendingCallNotes } from '../../services/callNotes';
import OrgBranchPicker from '../contacts/OrgBranchPicker';
import { useAuth } from '../../hooks/useAuth';
import { FiPhone, FiPhoneOff, FiMic, FiMicOff, FiDelete, FiPhoneCall, FiPhoneIncoming, FiUserPlus, FiUsers, FiX, FiSave, FiBell, FiVolume2, FiChevronDown } from 'react-icons/fi';
import toast from 'react-hot-toast';

// Ogohlantirish matnlari. 'mic-silent' — qurilmaning O'ZI jim (xom signal nol).
// 'encoder-silent' — qurilma ishlayapti (daraja qimirlayapti!), lekin ovoz
// uzatishga bermayapti; operator uchun eng chalg'ituvchi holat, shuning uchun
// matn ham boshqacha bo'lishi shart.
const AUDIO_ISSUE_TITLES = {
  'mic-silent': "⚠️ Mikrofondan ovoz kelmayapti — mijoz sizni ESHITMAYAPTI",
  'encoder-silent': "⚠️ Mikrofon ishlayapti, lekin ovoz mijozga KETMAYAPTI",
  network: "⚠️ Ovoz tarmoqqa ketmayapti — internetni tekshiring",
};

const AUDIO_ISSUE_HINTS = {
  'mic-silent': "Mikrofon Windows sozlamalarida o'chirilgan yoki noto'g'ri qurilma tanlangan bo'lishi mumkin.",
  'encoder-silent': "Ulanish qayta tiklanmoqda. O'zgarmasa — mikrofonni almashtiring yoki qo'ng'iroqni qayta qiling.",
};

// Mikrofon darajasi ko'rsatkichi. Komponent tashqarida — har renderда qayta
// yaratilib, animatsiyasi uzilib qolmasligi uchun.
function MicMeter({ level, warn }) {
  return (
    <div style={styles.meterWrap}>
      <div style={{
        ...styles.meterFill,
        width: `${Math.round(Math.min(1, level || 0) * 100)}%`,
        background: warn ? '#ef4444' : level > 0.6 ? '#f59e0b' : '#10b981',
      }} />
    </div>
  );
}

export default function SoftphoneWidget() {
  const { user } = useAuth();
  const [registered, setRegistered] = useState(false);
  const [callState, setCallState] = useState('idle');
  const [incomingCall, setIncomingCall] = useState(null);
  const [dialNumber, setDialNumber] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  // Telefon/planshetda vidjet ekranni egallab qolmasin — yig'ilgan holda
  // ochiladi (bitta tugma), kerak bo'lsa operator o'zi kengaytiradi.
  const [minimized, setMinimized] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 900,
  );
  const [peerNumber, setPeerNumber] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactId, setContactId] = useState(null);
  const [showSave, setShowSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveForm, setSaveForm] = useState({ phone: '', name: '', company: '', notes: '' });
  const [saveOrg, setSaveOrg] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferOps, setTransferOps] = useState([]);
  const [loadingOps, setLoadingOps] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [audioIssue, setAudioIssue] = useState(null); // null | 'mic-silent' | 'encoder-silent' | 'network'
  const [micDevices, setMicDevices] = useState([]);
  const [micDeviceId, setMicDeviceId] = useState(softphone.getMicDeviceId());
  const [showMicPanel, setShowMicPanel] = useState(false);
  const [micBusy, setMicBusy] = useState(false);
  // Ikkala holat ham operator kompyuteri tomonidagi muammo — bir xil qizil
  // ko'rinish va "qurilmani almashtirish" tugmasi bilan ko'rsatiladi.
  const isMicIssue = audioIssue === 'mic-silent' || audioIssue === 'encoder-silent';
  // Kiruvchi qo'ng'iroq lentasi: yashirilgan bo'lsa faqat ingichka chiziq qoladi.
  const [incomingHidden, setIncomingHidden] = useState(false);
  const [ringSeconds, setRingSeconds] = useState(0);
  const [barSlot, setBarSlot] = useState(null);
  const [ringVolume, setRingVolumeState] = useState(softphone.getRingVolume());
  const [ringOutput, setRingOutputState] = useState(softphone.getRingOutput());
  const [ringOutputs, setRingOutputs] = useState([]);
  const [dialMode, setDialMode] = useState('keypad'); // 'keypad' | 'directory'
  const [dirOps, setDirOps] = useState([]);
  const [loadingDir, setLoadingDir] = useState(false);
  const timerRef = useRef(null);
  const incomingCallRef = useRef(null);
  // Qo'ng'iroqdan keyingi ixtiyoriy izoh kartasi (null — yopiq).
  const [postNote, setPostNote] = useState(null);
  const postNoteRef = useRef(null);
  // Softphone callback'lari useEffect([user]) ichida bir marta yaratiladi va
  // state'ning ESKI qiymatini ko'radi — joriy raqam/ism/holat ref orqali o'qiladi.
  const liveRef = useRef({ peer: '', name: '', state: 'idle' });
  // Javob berilgan qo'ng'iroq ma'lumoti — tugaganda izoh kartasi shundan ochiladi.
  const callInfoRef = useRef(null);

  const resetCallState = () => {
    setCallState('idle');
    setIncomingCall(null);
    setIncomingHidden(false);
    incomingCallRef.current = null;
    setCallDuration(0);
    setMuted(false);
    setPeerNumber('');
    setContactName('');
    setContactId(null);
    // showSave/saveForm ataylab tozalanmaydi — qo'ng'iroq tugasa ham
    // yozib bo'lingan kontakt formasi yo'qolmasligi kerak.
    setShowTransfer(false);
    setTransferOps([]);
    setTransferring(false);
    stopTimer();
  };

  // Lenta layout ichidagi #cc-callbar-slot ga portal orqali chiqadi. Slot
  // sahifa OQIMIDA turgani uchun kontent tabiiy ravishda pastga suriladi —
  // hech narsa to'silmaydi. Slot topilmasa yuqoriga fixed qilib qo'yamiz.
  useEffect(() => {
    setBarSlot(document.getElementById('cc-callbar-slot'));
  }, []);

  // Jiringlash sekundlari — "qancha vaqtdan beri kutyapti". Modal majburlamasa
  // ham, 40 soniya turgani ko'rinsa operator o'zi javob beradi.
  useEffect(() => {
    if (callState !== 'incoming') { setRingSeconds(0); return; }
    const startedAt = Date.now();
    setRingSeconds(0);
    const t = setInterval(() => setRingSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [callState]);

  // Chiqish qurilmalari ro'yxati. Garnitura ulansa/uzilsa yangilanadi.
  useEffect(() => {
    let alive = true;
    const refresh = () => softphone.listAudioOutputs().then((l) => { if (alive) setRingOutputs(l); });
    refresh();
    const md = navigator.mediaDevices;
    if (md && md.addEventListener) md.addEventListener('devicechange', refresh);
    return () => {
      alive = false;
      if (md && md.removeEventListener) md.removeEventListener('devicechange', refresh);
    };
  }, []);

  // Brauzerning avtomatik ovoz siyosati: sahifada biror narsa BOSILMAGUNCHA
  // AudioContext jim turadi va jiringlash umuman eshitilmaydi. Har bosishda
  // uni tiriltiramiz (allaqachon ishlab tursa — hech narsa qilmaydi).
  useEffect(() => {
    const unlock = () => softphone.unlockAudio();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Qo'ng'iroqni o'tkazish uchun mos (online + ro'yxatdan o'tgan) operatorlarni yuklaymiz
  const openTransfer = async () => {
    setShowTransfer(true);
    setLoadingOps(true);
    try {
      const res = await statusAPI.availableOperators();
      setTransferOps(res.data.operators || []);
    } catch (e) {
      toast.error("Operatorlar ro'yxatini olishda xato");
      setTransferOps([]);
    } finally {
      setLoadingOps(false);
    }
  };

  const doTransfer = (op) => {
    setTransferring(true);
    softphone.transfer(op.sip_extension, {
      onSuccess: () => {
        if (callInfoRef.current) callInfoRef.current.transferred = true;
        toast.success(`Qo'ng'iroq ${op.full_name}ga o'tkazildi`);
        // Transfer qabul qilindi — bu operatorning sessiyasi tez orada tugaydi (onCallEnded).
      },
      onError: (cause) => {
        setTransferring(false);
        setShowTransfer(false);
        toast.error(`O'tkazib bo'lmadi: ${cause || "noma'lum"}. Qo'ng'iroq sizda qoldi.`);
      },
    });
  };

  // Faol qo'ng'iroq raqamini kontaktga saqlash
  const isInternalNumber = (n) => /^1\d{2}$/.test(String(n || ''));
  // Tashqi raqamlarda tugma doim ko'rinadi; kontakt allaqachon bor bo'lsa — nofaol.
  const showSaveContactBtn = !!peerNumber && !isInternalNumber(peerNumber);
  const contactAlreadySaved = !!contactId;

  // === Qo'ng'iroqdan keyingi izoh ===
  useEffect(() => {
    liveRef.current = { peer: peerNumber, name: contactName, state: callState };
    // Chiquvchida kontakt nomi javobdan keyin aniqlanishi mumkin.
    const info = callInfoRef.current;
    if (info && contactName && info.number === peerNumber) info.name = contactName;
  }, [peerNumber, contactName, callState]);

  useEffect(() => { postNoteRef.current = postNote; }, [postNote]);

  // Yangi qo'ng'iroq boshlansa (kiruvchi jiringladi yoki raqam terildi) va izoh
  // BO'SH bo'lsa — karta yopiladi. Matn yozilgan bo'lsa qoladi: yozilgani yo'qolmasin.
  useEffect(() => {
    const n = postNoteRef.current;
    if ((callState === 'incoming' || callState === 'outgoing') && n && !n.text.trim()) setPostNote(null);
  }, [callState]);

  const noteSaved = (tid) => {
    toast.success('Izoh saqlandi', tid ? { id: tid } : undefined);
    window.dispatchEvent(new CustomEvent('cc:call-note-saved'));
  };

  const noteFailed = (item, tid) => {
    toast.error(
      `Izoh saqlanmadi — qo'ng'iroq yozuvi topilmadi. Jurnaldagi izoh tugmasi orqali qo'shing: «${item.text.slice(0, 80)}»`,
      { id: tid, duration: 15000 },
    );
  };

  // Sahifa yangilanganda tugamay qolgan izohlar davom ettiriladi.
  useEffect(() => {
    if (!user?.id) return;
    resumePendingCallNotes(user.id, { onSaved: () => noteSaved(), onFailed: (item) => noteFailed(item) });
  }, [user?.id]);

  // Karta darhol yopiladi, saqlash fonda davom etadi: yozuvni cdrSync qo'ng'iroq
  // tugagandan ~5-10 s keyin yaratadi (services/callNotes.js).
  const submitPostNote = async (note) => {
    const text = (note?.text || '').trim();
    if (!text || !user?.id) return;
    const tid = toast.loading('Izoh saqlanmoqda…');
    const knownIds = await Promise.resolve(note.knownIdsPromise).catch(() => null);
    queueCallNote(
      { userId: user.id, role: user.role, number: note.number, direction: note.direction,
        durationSec: note.durationSec, knownIds: knownIds || null, text },
      { onSaved: () => noteSaved(tid), onFailed: (item) => noteFailed(item, tid) },
    );
  };

  // Javob berilgan TASHQI qo'ng'iroq tugaganda izoh kartasi ochiladi (majburiy emas).
  // Ichki (1XX) va boshqa operatorga o'tkazilgan qo'ng'iroqda ochilmaydi: o'tkazilgan
  // qo'ng'iroq jurnalda yangi operatorga yoziladi, bu operator unga izoh yoza olmaydi.
  const openPostCallNote = () => {
    const info = callInfoRef.current;
    callInfoRef.current = null; // transferda onCallEnded ikki marta kelishi mumkin
    if (!info || info.transferred || !info.number || isInternalNumber(info.number)) return;
    const prev = postNoteRef.current;
    if (prev && prev.text.trim()) submitPostNote(prev); // oldingi qo'ng'iroqqa yozilgan matn yo'qolmasin
    setPostNote({
      key: String(info.answeredAt),
      number: info.number,
      name: info.name,
      direction: info.direction,
      durationSec: Math.max(0, Math.round((Date.now() - info.answeredAt) / 1000)),
      knownIdsPromise: info.knownIdsPromise,
      text: '',
    });
  };

  const openSaveContact = () => {
    if (!peerNumber) return;
    setSaveOrg(null);
    setSaveForm({
      phone: peerNumber,
      // CallerID nomi bo'lsa oldindan to'ldiramiz
      name: contactName || '',
      company: '',
      notes: '',
    });
    setShowSave(true);
  };

  const submitSaveContact = async (e) => {
    e.preventDefault();
    if (saving) return;
    const name = saveForm.name.trim();
    if (!name) { toast.error('Ism majburiy'); return; }
    setSaving(true);
    try {
      const res = await contactsAPI.create({
        phone: saveForm.phone,
        name,
        ...(saveOrg || {}),
        notes: saveForm.notes.trim() || null,
      });
      const c = res.data?.contact;
      toast.success('Kontakt saqlandi');
      setShowSave(false);
      // Agar hali o'sha raqam bilan gaplashayotgan bo'lsak — panelda ism chiqsin
      if (c && peerNumber === saveForm.phone) {
        setContactName(c.name);
        setContactId(c.id);
      }
    } catch (err) {
      const msg = err.response?.data?.error;
      if (err.response?.status === 409) {
        toast.error(msg || 'Bu raqam allaqachon kontaktlarda bor');
        setShowSave(false);
        if (peerNumber === saveForm.phone) setContactId(err.response?.data?.existing_id || null);
      } else {
        toast.error(msg || 'Kontaktni saqlashda xato');
      }
    } finally {
      setSaving(false);
    }
  };

  // === Mikrofon qurilmasi paneli ===
  const openMicPanel = async (withTest = false) => {
    setShowMicPanel(true);
    try {
      // Qurilma NOMLARI faqat ruxsat berilgandan keyin ko'rinadi — shuning uchun
      // bo'sh qo'ng'iroqда avval mikrofonni yoqamiz (test rejimi).
      if (withTest) await softphone.startMicTest();
    } catch (e) {
      toast.error(e.message || "Mikrofonni ochib bo'lmadi");
    }
    setMicDevices(await softphone.listMicDevices());
    setMicDeviceId(softphone.getMicDeviceId());
  };

  const closeMicPanel = () => {
    setShowMicPanel(false);
    softphone.stopMicTest(); // qo'ng'iroq ketayotgan bo'lsa tegmaydi
    setMicLevel(0);
  };

  const chooseMic = async (deviceId) => {
    setMicBusy(true);
    try {
      await softphone.setMicDevice(deviceId);
      setMicDeviceId(deviceId);
      setMicDevices(await softphone.listMicDevices());
      toast.success('Mikrofon almashtirildi');
    } catch (e) {
      toast.error(e.message || "Mikrofonni almashtirishda xato");
    } finally {
      setMicBusy(false);
    }
  };

  // Test rejimi ochiq qolib mikrofon behuda yonib turmasligi uchun avtomatik yopamiz.
  useEffect(() => {
    if (!showMicPanel) return;
    const t = setTimeout(() => {
      if (!softphone.isInCall()) {
        softphone.stopMicTest();
        setMicLevel(0);
        setShowMicPanel(false);
      }
    }, 90000);
    return () => clearTimeout(t);
  }, [showMicPanel]);

  // Mikrofon qurilmalari ro'yxati (panel ichida)
  const micPanelBody = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <FiMic size={14} color="#94a3b8" />
        <MicMeter level={micLevel} warn={audioIssue === 'mic-silent'} />
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
        Gapiring — chiziq harakatlanishi kerak. Harakatlanmasa mikrofon ovoz bermayapti.
      </div>
      {micDevices.length === 0 ? (
        <div style={styles.transferMsg}>Qurilma topilmadi</div>
      ) : (
        <div style={styles.transferList}>
          {micDevices.map((d) => (
            <button
              key={d.deviceId}
              style={{
                ...styles.transferItem,
                borderColor: d.deviceId === micDeviceId ? '#10b981' : '#2d3a4f',
              }}
              onClick={() => chooseMic(d.deviceId)}
              disabled={micBusy}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: d.deviceId === micDeviceId ? '#10b981' : '#475569',
              }} />
              <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                {d.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );

  useEffect(() => {
    if (!user || !user.sip_extension) return;

    const wsUrl = `wss://${window.location.host}/ws`;
    const sipDomain = window.location.hostname;
    const password = `Op3rator${user.sip_extension}WebRTC!`;

    softphone.connect({
      extension: user.sip_extension,
      password,
      wsUrl,
      sipDomain,
      onRegistered: () => {
        setRegistered(true);
        toast.success("Telefoniya tizimiga ulandi");
      },
      onUnregistered: (cause) => {
        setRegistered(false);
        if (cause) toast.error(`Telefoniya uzildi: ${cause}`);
      },
      onIncomingCall: async (call) => {
        console.log('Incoming call:', call.from);
        setIncomingCall(call);
        incomingCallRef.current = call;
        setPeerNumber(call.from);
        setCallState('incoming');
        setIncomingHidden(false);
        // Panel ataylab MAJBURAN ochilmaydi — operator nima qilayotgan bo'lsa,
        // shuni davom ettiradi; qo'ng'iroq yuqoridagi lentada ko'rinadi.
        // Nomni aniqlash: ichki raqam (1XX) → operator ismi; aks holda kontakt qidiruvi.
        setContactName('');
        setContactId(null);
        const from = String(call.from || '');
        try {
          if (/^1\d{2}$/.test(from)) {
            // Ichki operator raqami — bazadan operator ismini olamiz
            const res = await statusAPI.getAll();
            const op = (res.data.operators || []).find(o => String(o.sip_extension) === from);
            if (op) { setContactName(op.full_name); return; }
          }
          const res = await contactsAPI.getByPhone(from);
          if (res.data?.contact?.name) { setContactName(res.data.contact.name); setContactId(res.data.contact.id); }
          else if (call.displayName && !/^Extension\s/i.test(call.displayName)) setContactName(call.displayName);
        } catch (e) {
          if (call.displayName && !/^Extension\s/i.test(call.displayName)) setContactName(call.displayName);
        }
      },
      onCallAnswered: () => {
        console.log('Call answered');
        const live = liveRef.current;
        const number = live.peer;
        const external = !!number && !isInternalNumber(number);
        callInfoRef.current = {
          number,
          name: live.name,
          direction: live.state === 'incoming' ? 'inbound' : 'outbound',
          answeredAt: Date.now(),
          transferred: false,
          // Shu paytdagi mavjud yozuvlar — keyin paydo bo'lgan yangisi aynan shu qo'ng'iroq.
          knownIdsPromise: external ? snapshotKnownIds(number) : Promise.resolve([]),
        };
        callInfoRef.current.knownIdsPromise.catch(() => {});
        setCallState('active');
        setIncomingCall(null);
        incomingCallRef.current = null;
        setCallDuration(0);
        startTimer();
      },
      onCallProgress: () => {
        console.log('Call progress');
        setCallState(prev => (prev === 'outgoing' || prev === 'idle') ? 'outgoing' : prev);
      },
      // Mikrofon darajasi (0..1) — operator o'z ovozi ketayotganini KO'RADI.
      onMicLevel: (lvl) => {
        setMicLevel(prev => (Math.abs(prev - lvl) > 0.03 || (lvl === 0 && prev !== 0) ? lvl : prev));
      },
      // Audio nosozligi aniqlansa — ekranda ogohlantirish.
      onAudioIssue: (kind) => setAudioIssue(kind),
      onCallEnded: () => {
        console.log('Call ended - resetting state');
        resetCallState();
        openPostCallNote();
      },
      onCallFailed: (cause) => {
        console.log('Call failed:', cause);
        callInfoRef.current = null;
        resetCallState();
        if (cause && cause !== 'Canceled' && cause !== 'Rejected') {
          toast.error(`Qo'ng'iroq amalga oshmadi: ${cause}`);
        }
      },
    });

    return () => {
      softphone.disconnect();
      stopTimer();
    };
  }, [user]);
  // Click-to-call event listener
  useEffect(() => {
    const handleTriggerCall = async (event) => {
      const number = event.detail?.number;
      if (!number) return;

      if (!softphone.isRegistered()) {
        toast.error("Telefoniya ulanmagan");
        return;
      }
      if (softphone.isInCall()) {
        toast.error("Boshqa qo'ng'iroq faol");
        return;
      }

      try {
        await softphone.call(number);
        setCallState('outgoing');
        setPeerNumber(number);
        setMinimized(false);
        // Kontakt nomini qidirish
        setContactId(null);
        contactsAPI.getByPhone(number).then(res => {
          if (res.data?.contact?.name) { setContactName(res.data.contact.name); setContactId(res.data.contact.id); }
          else setContactName('');
        }).catch(() => setContactName(''));
      } catch (err) {
        toast.error(err.message);
      }
    };
    
    window.addEventListener('softphone:call', handleTriggerCall);
    return () => window.removeEventListener('softphone:call', handleTriggerCall);
  }, []);


  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  // Ichki qo'ng'iroq uchun bo'sh (online + ro'yxatdan o'tgan) xodimlarni yuklaymiz
  const openDirectory = async () => {
    setDialMode('directory');
    setLoadingDir(true);
    try {
      const res = await statusAPI.availableOperators();
      setDirOps(res.data.operators || []);
    } catch (e) {
      toast.error("Xodimlar ro'yxatini olishda xato");
      setDirOps([]);
    } finally {
      setLoadingDir(false);
    }
  };

  // Xodimning ichki raqamiga to'g'ridan-to'g'ri qo'ng'iroq (dialplan _1XX orqali)
  const callOperator = async (op) => {
    if (!registered) { toast.error("Telefoniya ulanmagan"); return; }
    if (callState !== 'idle') { toast.error("Boshqa qo'ng'iroq faol"); return; }
    try {
      await softphone.call(op.sip_extension);
      setCallState('outgoing');
      setPeerNumber(op.sip_extension);
      setContactName(op.full_name);
      setMinimized(false);
    } catch (err) {
      toast.error(err.message);
      resetCallState();
    }
  };

  const handleDial = async () => {
    if (!dialNumber) return;
    if (!registered) { toast.error("Telefoniya ulanmagan"); return; }
    if (callState !== 'idle') { toast.error("Boshqa qo'ng'iroq faol"); return; }
    try {
      await softphone.call(dialNumber);
      setCallState('outgoing');
      setPeerNumber(dialNumber);
      setMinimized(false);
      // Kontakt nomini qidirish
      setContactId(null);
      contactsAPI.getByPhone(dialNumber).then(res => {
        if (res.data?.contact?.name) { setContactName(res.data.contact.name); setContactId(res.data.contact.id); }
        else setContactName('');
      }).catch(() => setContactName(''));
    } catch (err) {
      toast.error(err.message);
      resetCallState();
    }
  };

  const handleAnswer = () => {
    const call = incomingCallRef.current || incomingCall;
    if (call) {
      console.log('Answering call');
      call.accept();
    } else {
      console.warn('No incoming call to answer');
    }
  };

  const handleReject = () => {
    const call = incomingCallRef.current || incomingCall;
    if (call) {
      console.log('Rejecting call');
      call.reject();
    }
    resetCallState();
  };

  // Yashirish = rad etish EMAS. SIP'ga hech narsa yuborilmaydi: qo'ng'iroq
  // navbatda qolaveradi va boshqa operatorlarda jiringlashda davom etadi.
  // Faqat ovoz o'chadi va lenta ingichka chiziqqa yig'iladi.
  const handleHideIncoming = () => {
    setIncomingHidden(true);
    softphone.silenceRing();
  };

  const handleHangup = () => {
    console.log('Hangup button pressed');
    softphone.hangup();
    resetCallState();
  };

  const toggleMute = () => {
    const newMute = !muted;
    softphone.mute(newMute);
    setMuted(newMute);
  };

  const handleDigit = (d) => {
    if (callState === 'active') {
      softphone.sendDTMF(d);
    } else if (callState === 'idle') {
      setDialNumber(dialNumber + d);
    }
  };

  const handleBackspace = () => {
    setDialNumber(dialNumber.slice(0, -1));
  };

  if (!user?.sip_extension) return null;

  // Kontakt saqlash oynasi — barcha holatlarda ustidan ko'rinadi (qo'ng'iroq
  // tugab qolsa ham yozilgan ma'lumot yo'qolmasin).
  const saveContactModal = showSave ? (
    <div style={styles.saveOverlay} onClick={() => !saving && setShowSave(false)}>
      <form style={styles.saveCard} onClick={e => e.stopPropagation()} onSubmit={submitSaveContact}>
        <div style={styles.transferHead}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>Kontaktga saqlash</span>
          <button type="button" style={styles.closeBtn} onClick={() => setShowSave(false)} disabled={saving}>
            <FiX size={16} />
          </button>
        </div>

        <div style={styles.savePhone}>{saveForm.phone}</div>

        <label style={styles.saveLabel}>Ism <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          style={styles.saveInput}
          autoFocus
          value={saveForm.name}
          onChange={e => setSaveForm({ ...saveForm, name: e.target.value })}
          placeholder="Mijoz ismi"
        />

        <OrgBranchPicker value={saveOrg} onChange={setSaveOrg} />

        <label style={styles.saveLabel}>Izoh</label>
        <textarea
          style={{ ...styles.saveInput, minHeight: 60, resize: 'vertical' }}
          value={saveForm.notes}
          onChange={e => setSaveForm({ ...saveForm, notes: e.target.value })}
          placeholder="Ixtiyoriy"
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button type="button" style={styles.saveCancelBtn} onClick={() => setShowSave(false)} disabled={saving}>
            Bekor qilish
          </button>
          <button type="submit" style={styles.saveSubmitBtn} disabled={saving || !saveForm.name.trim()}>
            {saving ? 'Saqlanmoqda…' : 'Saqlash'}
          </button>
        </div>
      </form>
    </div>
  ) : null;

  // KIRUVCHI QO'NG'IROQ KARTASI. Avval bu ekranni to'liq qoplaydigan modal edi
  // (inset:0 + blur): navbat sozlamasi ringall bo'lgani uchun u BIR VAQTDA
  // hamma operatorda ochilardi va queues.conf timeout=50 tufayli panelni 50
  // soniyagacha bloklardi.
  // Keyingi urinish — sahifa oqimidagi to'liq kenglikdagi lenta — layoutni
  // 52px pastga surib, sarlavha va tugmalarni SAKRATARDI. Endi karta layoutdagi
  // balandligi NOL sticky slotda suzadi: hech narsa surilmaydi, sahifa
  // aylantirilganda ham karta ekranning o'sha joyida qoladi, ostidagi panel
  // to'liq ishlaydi.
  const incomingBar = callState !== 'incoming' ? null : incomingHidden ? (
    <div style={styles.barMini} onClick={() => setIncomingHidden(false)} title="Qo'ng'iroqni qayta ochish">
      <span style={styles.barDot} />
      <FiPhoneIncoming size={12} />
      <span style={{ fontWeight: 600 }}>{contactName || peerNumber}</span>
      <span style={styles.barTimer}>{formatTime(ringSeconds)}</span>
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
        ochish <FiChevronDown size={12} />
      </span>
    </div>
  ) : (
    <div style={styles.bar}>
      <span style={styles.barDot} />
      <FiPhoneIncoming size={18} color="#10b981" style={{ flexShrink: 0 }} />
      <div style={styles.barWho}>
        <span style={styles.barLabel}>Kiruvchi</span>
        <span style={styles.barName}>{contactName || peerNumber}</span>
        {contactName && <span style={styles.barNum}>{peerNumber}</span>}
      </div>
      <span style={styles.barTimer}>{formatTime(ringSeconds)}</span>
      <div style={{ flex: 1, minWidth: 8 }} />
      <button style={styles.barAnswer} onClick={handleAnswer}>
        <FiPhone size={15} /> Javob berish
      </button>
      <button style={styles.barReject} onClick={handleReject}>
        <FiPhoneOff size={15} /> Rad etish
      </button>
      <button style={styles.barHide} onClick={handleHideIncoming} title="Yashirish — qo'ng'iroq rad etilmaydi, faqat ovoz o'chadi">
        <FiX size={16} />
      </button>
    </div>
  );

  const barNode = !incomingBar ? null
    : barSlot ? createPortal(incomingBar, barSlot)
    : <div style={styles.barFallback}>{incomingBar}</div>;

  // Izoh kartasi vidjetning chap yonida (vidjet: right 20, kengligi 280;
  // yig'ilganda 56px tugma). Faol qo'ng'iroq paneli doim to'liq kenglikda.
  const widgetCollapsed = minimized && callState !== 'active' && callState !== 'outgoing';
  const postNoteNode = postNote ? (
    <PostCallNote
      note={postNote}
      right={widgetCollapsed ? 88 : 312}
      onChange={(text) => setPostNote((n) => (n ? { ...n, text } : n))}
      onSave={() => { submitPostNote(postNote); setPostNote(null); }}
      onClose={() => setPostNote(null)}
    />
  ) : null;

  if (callState === 'active' || callState === 'outgoing') {
    return (
      <>
      {saveContactModal}
    {barNode}
    {postNoteNode}
      <div style={styles.activeCall}>
        <div style={styles.callHeader}>
          <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {callState === 'outgoing' ? "Qo'ng'iroq qilinmoqda..." : "Qo'ng'iroqda"}
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{contactName || peerNumber}</div>
          {contactName && <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>{peerNumber}</div>}
          {callState === 'active' && (
            <div style={{ fontSize: 14, color: '#10b981', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
              {formatTime(callDuration)}
            </div>
          )}
        </div>

        {/* Ovoz ketayotganini KO'RSATUVCHI indikator — mijoz shikoyat qilishini kutmaymiz */}
        {callState === 'active' && !muted && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <FiMic size={13} color={isMicIssue ? '#ef4444' : '#64748b'} />
            <MicMeter level={micLevel} warn={isMicIssue} />
          </div>
        )}

        {audioIssue && !muted && (
          <div style={{
            ...styles.audioAlert,
            background: isMicIssue ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
            borderColor: isMicIssue ? '#ef4444' : '#f59e0b',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: isMicIssue ? '#fca5a5' : '#fcd34d' }}>
              {AUDIO_ISSUE_TITLES[audioIssue] || "⚠️ Ovoz tarmoqqa ketmayapti — internetni tekshiring"}
            </div>
            {isMicIssue && (
              <>
                <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 4, lineHeight: 1.4 }}>
                  {AUDIO_ISSUE_HINTS[audioIssue]}
                </div>
                <button style={styles.alertBtn} onClick={() => (showMicPanel ? closeMicPanel() : openMicPanel(false))}>
                  Mikrofonni almashtirish
                </button>
              </>
            )}
          </div>
        )}

        {showMicPanel && (
          <div style={styles.transferPanel}>
            <div style={styles.transferHead}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>Mikrofon qurilmasi</span>
              <button style={styles.closeBtn} onClick={closeMicPanel}><FiX size={16} /></button>
            </div>
            {micPanelBody}
          </div>
        )}

        <div style={styles.callActions}>
          <button style={{ ...styles.iconBtn, background: muted ? '#ef4444' : '#1e293b' }} onClick={toggleMute} title={muted ? 'Ovozni yoqish' : "Mikrofonni o'chirish"}>
            {muted ? <FiMicOff size={18} /> : <FiMic size={18} />}
          </button>
          {showSaveContactBtn && (
            <button
              style={{
                ...styles.iconBtn,
                background: '#1e293b',
                ...(contactAlreadySaved ? styles.iconBtnDisabled : {}),
              }}
              onClick={openSaveContact}
              disabled={contactAlreadySaved}
              title={contactAlreadySaved ? 'Bu raqam allaqachon kontaktlarda bor' : 'Kontaktga saqlash'}
            >
              <FiSave size={18} />
            </button>
          )}
          {callState === 'active' && (
            <button style={{ ...styles.iconBtn, background: showTransfer ? '#3b82f6' : '#1e293b' }} onClick={() => (showTransfer ? setShowTransfer(false) : openTransfer())} title="Boshqa operatorga o'tkazish" disabled={transferring}>
              <FiUserPlus size={18} />
            </button>
          )}
          <button style={{ ...styles.iconBtn, background: '#ef4444', padding: '14px 20px' }} onClick={handleHangup}>
            <FiPhoneOff size={20} />
          </button>
        </div>

        {showTransfer && (
          <div style={styles.transferPanel}>
            <div style={styles.transferHead}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>Kimga o'tkazamiz?</span>
              <button style={styles.closeBtn} onClick={() => setShowTransfer(false)}><FiX size={16} /></button>
            </div>
            {transferring ? (
              <div style={styles.transferMsg}>O'tkazilmoqda…</div>
            ) : loadingOps ? (
              <div style={styles.transferMsg}>Yuklanmoqda…</div>
            ) : transferOps.length === 0 ? (
              <div style={styles.transferMsg}>Hozir bo'sh operator yo'q</div>
            ) : (
              <div style={styles.transferList}>
                {transferOps.map((op) => (
                  <button key={op.id} style={styles.transferItem} onClick={() => doTransfer(op)}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                    <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.full_name}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{op.sip_extension}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </>
    );
  }

  if (minimized) {
    return (
      <>
      {saveContactModal}
    {barNode}
    {postNoteNode}
      <button style={styles.minimized} onClick={() => setMinimized(false)}>
        <FiPhone size={20} />
        {!registered && <span style={styles.offlineDot} />}
      </button>
      </>
    );
  }

  return (
    <>
    {saveContactModal}
    {barNode}
    {postNoteNode}
    <div style={styles.dialer}>
      <div style={styles.dialerHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: registered ? '#10b981' : '#ef4444' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
            Ext {user.sip_extension} · {registered ? 'Onlayn' : 'Oflayn'}
          </span>
        </div>
        <button style={styles.closeBtn} onClick={() => setMinimized(true)}>−</button>
      </div>

      <div style={styles.modeTabs}>
        <button
          style={{ ...styles.modeTab, ...(dialMode === 'keypad' ? styles.modeTabActive : {}) }}
          onClick={() => setDialMode('keypad')}
        >
          <FiPhoneCall size={14} /> Raqam
        </button>
        <button
          style={{ ...styles.modeTab, ...(dialMode === 'directory' ? styles.modeTabActive : {}) }}
          onClick={openDirectory}
        >
          <FiUsers size={14} /> Xodimlar
        </button>
      </div>

      {dialMode === 'keypad' ? (
        <>
          <div style={styles.display}>
            <input
              style={styles.displayInput}
              placeholder="Raqam tering..."
              value={dialNumber}
              onChange={e => setDialNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleDial()}
            />
            {dialNumber && (
              <button style={styles.backspace} onClick={handleBackspace}><FiDelete size={16} /></button>
            )}
          </div>

          <div style={styles.keypad}>
            {['1','2','3','4','5','6','7','8','9','*','0','#'].map(d => (
              <button key={d} style={styles.key} onClick={() => handleDigit(d)}>{d}</button>
            ))}
          </div>

          <button
            style={styles.dialBtn}
            onClick={handleDial}
            disabled={!registered || !dialNumber || callState === 'incoming'}
            title={callState === 'incoming' ? "Avval kiruvchi qo'ng'iroqni yakunlang" : undefined}
          >
            <FiPhoneCall size={18} />
            Qo'ng'iroq qilish
          </button>
        </>
      ) : (
        <div style={styles.dirWrap}>
          {loadingDir ? (
            <div style={styles.transferMsg}>Yuklanmoqda…</div>
          ) : dirOps.length === 0 ? (
            <div style={styles.transferMsg}>Hozir bo'sh xodim yo'q</div>
          ) : (
            <div style={styles.dirList}>
              {dirOps.map((op) => (
                <button key={op.id} style={styles.transferItem} onClick={() => callOperator(op)} disabled={!registered}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                  <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.full_name}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{op.sip_extension}</span>
                  <FiPhoneCall size={14} color="#10b981" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mikrofonni oldindan tekshirish — muammoni mijoz shikoyat qilishidan oldin topish */}
      <div style={styles.micFooter}>
        {!showMicPanel ? (
          <button style={styles.micTestBtn} onClick={() => openMicPanel(true)}>
            <FiMic size={13} /> Mikrofonni tekshirish
          </button>
        ) : (
          <>
            <div style={styles.transferHead}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>Mikrofon</span>
              <button style={styles.closeBtn} onClick={closeMicPanel}><FiX size={16} /></button>
            </div>
            {micPanelBody}
          </>
        )}
      </div>

      {/* Qo'ng'iroq ohangi. Lenta ekranni egallamagani uchun OVOZ asosiy
          ogohlantirishga aylandi — operator uni o'z xonasiga moslay olsin. */}
      <div style={styles.ringRow}>
        <div style={styles.ringHead}>
          <FiBell size={13} />
          <span style={{ flex: 1 }}>Qo'ng'iroq ovozi · Marimba</span>
          <button style={styles.ringTestBtn} onClick={() => softphone.testRing()} title="Tinglab ko'rish">
            <FiVolume2 size={12} /> Tinglash
          </button>
        </div>
        <div style={{ ...styles.ringVolRow, marginTop: 0 }}>
          <FiVolume2 size={13} color="#64748b" />
          <input
            type="range" min="0" max="100" step="5"
            value={Math.round(ringVolume * 100)}
            style={styles.ringSlider}
            onChange={(e) => {
              const v = Number(e.target.value) / 100;
              setRingVolumeState(v);
              softphone.setRingVolume(v);
            }}
            onMouseUp={() => softphone.testRing()}
          />
          <span style={styles.ringVolVal}>{Math.round(ringVolume * 100)}%</span>
        </div>

        {/* ENG MUHIM SOZLAMA. Quloqchin stolda yotgan bo'lsa, ovoz baribir
            quloqchinga ketadi va hech kim eshitmaydi — balandlikni oshirish
            yordam bermaydi. Shuning uchun jiringlashni kompyuter karnayiga
            HAM yuboramiz (Zoom/Teams dagi "ring on separate device" kabi). */}
        {softphone.supportsRingOutput() && (
          <>
            <select
              style={{ ...styles.ringSelect, marginTop: 8 }}
              value={ringOutput}
              onChange={(e) => {
                const v = e.target.value;
                setRingOutputState(v);
                softphone.setRingOutput(v);
                if (v) setTimeout(() => softphone.testRing(), 250);
              }}
            >
              <option value="">Faqat asosiy qurilmada jiringlasin</option>
              {ringOutputs.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>Karnay: {d.label}</option>
              ))}
            </select>
            <div style={styles.ringHint}>
              Quloqchin yechilgan bo'lsa ham eshitish uchun kompyuter karnayini tanlang.
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}

const styles = {
  dialer: {
    position: 'fixed', bottom: 20, right: 20, width: 280,
    background: '#1e293b', border: '1px solid #2d3a4f',
    borderRadius: 16, padding: 16, zIndex: 999,
    boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
  },
  dialerHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 12,
  },
  closeBtn: {
    background: 'transparent', border: 'none', color: '#94a3b8',
    fontSize: 20, cursor: 'pointer', width: 24, height: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 6,
  },
  modeTabs: { display: 'flex', gap: 6, marginBottom: 12 },
  modeTab: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '8px 0', background: '#0f172a', border: '1px solid #2d3a4f',
    borderRadius: 8, color: '#94a3b8', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  modeTabActive: { background: '#3b82f6', borderColor: '#3b82f6', color: '#fff' },
  dirWrap: { minHeight: 120 },
  dirList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' },
  display: { position: 'relative', marginBottom: 12 },
  displayInput: {
    width: '100%', padding: '12px 40px 12px 14px',
    background: '#0f172a', border: '1px solid #2d3a4f',
    borderRadius: 10, color: '#f1f5f9', fontSize: 18,
    fontWeight: 600, letterSpacing: 1, outline: 'none',
    fontFamily: 'inherit',
  },
  backspace: {
    position: 'absolute', right: 8, top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent', border: 'none', color: '#94a3b8',
    cursor: 'pointer', padding: 6, borderRadius: 6,
  },
  keypad: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
    marginBottom: 12,
  },
  key: {
    padding: '14px 0', background: '#0f172a',
    border: '1px solid #2d3a4f', borderRadius: 10,
    color: '#f1f5f9', fontSize: 18, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
    fontFamily: 'inherit',
  },
  dialBtn: {
    width: '100%', padding: 12, background: '#10b981',
    border: 'none', borderRadius: 10, color: 'white',
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, transition: 'all 0.15s',
    fontFamily: 'inherit',
  },
  minimized: {
    position: 'fixed', bottom: 20, right: 20,
    width: 56, height: 56, borderRadius: '50%',
    background: '#3b82f6', border: 'none', color: 'white',
    cursor: 'pointer', zIndex: 999,
    boxShadow: '0 8px 24px rgba(59,130,246,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  offlineDot: {
    position: 'absolute', top: 4, right: 4,
    width: 10, height: 10, borderRadius: '50%',
    background: '#ef4444', border: '2px solid white',
  },
  activeCall: {
    position: 'fixed', bottom: 20, right: 20, width: 280,
    background: '#1e293b', border: '1px solid #2d3a4f',
    borderRadius: 16, padding: 20, zIndex: 999,
    boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
  },
  callHeader: { textAlign: 'center', marginBottom: 20 },
  callActions: { display: 'flex', justifyContent: 'center', gap: 12 },
  transferPanel: {
    marginTop: 16, paddingTop: 14, borderTop: '1px solid #2d3a4f',
  },
  transferHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10,
  },
  transferMsg: { fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '10px 0' },
  transferList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' },
  transferItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', background: '#0f172a', border: '1px solid #2d3a4f',
    borderRadius: 10, color: '#f1f5f9', fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  saveOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1001, backdropFilter: 'blur(4px)', padding: 16,
  },
  saveCard: {
    background: '#1e293b', border: '1px solid #2d3a4f',
    borderRadius: 16, padding: 20, width: 340, maxWidth: '100%',
    boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  savePhone: {
    background: '#0f172a', border: '1px solid #2d3a4f', borderRadius: 10,
    padding: '10px 12px', color: '#f1f5f9', fontSize: 17, fontWeight: 700,
    fontFamily: 'monospace', letterSpacing: 1, textAlign: 'center', marginBottom: 14,
  },
  saveLabel: {
    display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8',
    marginBottom: 5, marginTop: 10,
  },
  saveInput: {
    width: '100%', padding: '10px 12px', background: '#0f172a',
    border: '1px solid #2d3a4f', borderRadius: 10, color: '#f1f5f9',
    fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  },
  saveCancelBtn: {
    flex: 1, padding: 11, background: '#0f172a', border: '1px solid #2d3a4f',
    borderRadius: 10, color: '#94a3b8', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  saveSubmitBtn: {
    flex: 1, padding: 11, background: '#10b981', border: 'none',
    borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  iconBtn: {
    width: 48, height: 48, borderRadius: '50%',
    border: 'none', color: 'white', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  meterWrap: {
    flex: 1, height: 6, background: '#0f172a', borderRadius: 3,
    overflow: 'hidden', border: '1px solid #2d3a4f',
  },
  meterFill: {
    height: '100%', borderRadius: 3, transition: 'width 0.12s linear',
  },
  audioAlert: {
    border: '1px solid', borderRadius: 10, padding: '10px 12px', marginBottom: 14,
  },
  alertBtn: {
    marginTop: 8, width: '100%', padding: '7px 0', background: '#1e293b',
    border: '1px solid #475569', borderRadius: 8, color: '#f1f5f9',
    fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  micFooter: {
    marginTop: 12, paddingTop: 12, borderTop: '1px solid #2d3a4f',
  },
  micTestBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, padding: '8px 0', background: 'transparent', border: '1px solid #2d3a4f',
    borderRadius: 8, color: '#94a3b8', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  iconBtnDisabled: {
    opacity: 0.4, cursor: 'not-allowed', color: '#94a3b8',
  },
  // --- Kiruvchi qo'ng'iroq kartasi ---
  // Sarlavha OSTIDA, kontent ustida suzadi. Joylashtiruvchi konteyner
  // (layoutdagi #cc-callbar-slot) balandligi nol va sticky — shuning uchun
  // karta layoutni surmaydi va sahifa aylantirilganda joyidan qimirlamaydi.
  // Fon SHAFFOF EMAS: ostidagi matn ko'rinib turmasligi kerak.
  bar: {
    margin: '10px auto 0', maxWidth: 760, width: 'calc(100% - 48px)',
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px', minHeight: 52, overflow: 'hidden',
    borderRadius: 14,
    // Qattiq fon + ustidan yashil tus: gradient chizilmasa ham karta
    // SHAFFOF bo'lib qolmaydi, ostidagi matn ko'rinib ketmaydi.
    backgroundColor: '#1e293b',
    backgroundImage: 'linear-gradient(90deg, rgba(16,185,129,0.22), rgba(16,185,129,0) 45%)',
    border: '1px solid rgba(16,185,129,0.45)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
    animation: 'ccDropIn 0.22s ease',
  },
  barMini: {
    margin: '10px auto 0', maxWidth: 420, width: 'calc(100% - 48px)',
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 12px', fontSize: 12, color: '#94a3b8', cursor: 'pointer',
    borderRadius: 10,
    backgroundColor: '#1e293b',
    border: '1px solid rgba(16,185,129,0.35)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
  },
  barDot: {
    width: 9, height: 9, borderRadius: '50%', background: '#10b981', flexShrink: 0,
    animation: 'ccPulse 1.6s ease-in-out infinite',
  },
  barWho: { display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 },
  barLabel: {
    fontSize: 11, fontWeight: 700, color: '#6ee7b7',
    textTransform: 'uppercase', letterSpacing: 0.6, flexShrink: 0,
  },
  barName: {
    fontSize: 15, fontWeight: 700, color: '#f1f5f9', minWidth: 0,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  barNum: { fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 },
  barTimer: { fontSize: 13, color: '#94a3b8', fontVariantNumeric: 'tabular-nums', flexShrink: 0 },
  barAnswer: {
    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
    padding: '9px 16px', background: '#10b981', border: 'none', borderRadius: 10,
    color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  barReject: {
    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
    padding: '9px 16px', background: '#ef4444', border: 'none', borderRadius: 10,
    color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  barHide: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: 32, height: 32, background: 'transparent', border: '1px solid #2d3a4f',
    borderRadius: 8, color: '#94a3b8', cursor: 'pointer',
  },
  // Layoutda slot topilmasa (kutilmagan holat) — karta ekranga qadaladi.
  barFallback: { position: 'fixed', top: 76, left: 0, right: 0, zIndex: 1000 },

  // --- Qo'ng'iroq ohangi sozlamalari ---
  ringRow: { marginTop: 10, paddingTop: 10, borderTop: '1px solid #2d3a4f' },
  ringHead: {
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
    fontSize: 12, fontWeight: 600, color: '#94a3b8',
  },
  ringTestBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '4px 8px', background: 'transparent', border: '1px solid #2d3a4f',
    borderRadius: 6, color: '#94a3b8', fontSize: 11, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  ringSelect: {
    width: '100%', padding: '7px 8px', background: '#0f172a',
    border: '1px solid #2d3a4f', borderRadius: 8, color: '#f1f5f9',
    fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
  },
  ringVolRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
  ringHint: { fontSize: 11, color: '#64748b', marginTop: 6, lineHeight: 1.4 },
  ringSlider: { flex: 1, accentColor: '#10b981', cursor: 'pointer' },
  ringVolVal: {
    fontSize: 11, color: '#94a3b8', width: 34, textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
};
